import {
  AppAdminFormLabel,
  AppButton,
  AppPagination,
  AppSelect,
  AppTable,
  SearchTextField,
} from '@/components/atoms';
import { Search } from '@/components/atoms/AppIcon';
import { DEFAULT_PER_PAGE, EMPTY_TEXT_FILTERED } from '@/constants';
import { useAdminInsurerAttentionInit, useAdminInsurerList } from '@/hooks/admin';
import { Insurer, InsurerQuery } from '@/types/admin-insurer';
import { Empty, TableProps } from 'antd';
import { useEffect, useState } from 'react';

interface FilterState {
  search_insurer_number: string;
  search_insurer_name: string;
  search_insurer_name_kana: string;
  search_insurer_type_id: string;
  prefecture_id: string;
}

const INITIAL_FILTERS: FilterState = {
  search_insurer_number: '',
  search_insurer_name: '',
  search_insurer_name_kana: '',
  search_insurer_type_id: '',
  prefecture_id: '',
};

interface Props {
  onSelect: (insurer: Insurer) => void;
  initialSearch?: string;
}

const InsuranceCompanySearch = ({ onSelect, initialSearch }: Props) => {
  const { data: initData } = useAdminInsurerAttentionInit();

  const [inputFilters, setInputFilters] = useState<FilterState>({
    ...INITIAL_FILTERS,
    search_insurer_number: initialSearch ?? '',
  });
  const [queryFilters, setQueryFilters] = useState<InsurerQuery>(
    initialSearch ? { search_insurer_number: initialSearch } : {}
  );
  const [page, setPage] = useState(1);

  useEffect(() => {
    setInputFilters(prev => ({ ...prev, search_insurer_number: initialSearch ?? '' }));
    setQueryFilters(initialSearch ? { search_insurer_number: initialSearch } : {});
    setPage(1);
  }, [initialSearch]);

  const { data, isLoading } = useAdminInsurerList({
    page: String(page),
    per_page: String(DEFAULT_PER_PAGE),
    ...queryFilters,
  });

  const prefectureOptions = initData?.prefectures?.map(p => ({ value: p.id, label: p.name }));
  const insurerTypeOptions = initData?.insurer_types?.map(t => ({ value: t.id, label: t.name }));

  const handleChange = (key: keyof FilterState, value: string) => {
    setInputFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    setQueryFilters({
      search_insurer_number: inputFilters.search_insurer_number || undefined,
      search_insurer_name: inputFilters.search_insurer_name || undefined,
      search_insurer_name_kana: inputFilters.search_insurer_name_kana || undefined,
      search_insurer_type_id: inputFilters.search_insurer_type_id || undefined,
      prefecture_id: inputFilters.prefecture_id || undefined,
    });
    setPage(1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handleSearch();
    }
  };

  const columns: TableProps<Insurer>['columns'] = [
    {
      title: '保険者番号',
      dataIndex: 'insurer_number_full',
      key: 'insurer_number',
    },
    {
      title: '保険者名称',
      dataIndex: 'insurer_name',
      key: 'insurer_name',
    },
    {
      title: '保険種別',
      key: 'insurer_type',
      render: (_: unknown, r: Insurer) => r.insurer_type?.name ?? '-',
    },
    {
      title: '都道府県',
      dataIndex: 'address1',
      key: 'address1',
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div>
          <AppAdminFormLabel label="保険者番号" />
          <SearchTextField
            placeholder="保険者番号"
            value={inputFilters.search_insurer_number}
            onChange={e => handleChange('search_insurer_number', e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>
        <div>
          <AppAdminFormLabel label="保険者名称" />
          <SearchTextField
            placeholder="保険者名称"
            value={inputFilters.search_insurer_name}
            onChange={e => handleChange('search_insurer_name', e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>
        <div>
          <AppAdminFormLabel label="保険者名カナ" />
          <SearchTextField
            placeholder="保険者名カナ"
            value={inputFilters.search_insurer_name_kana}
            onChange={e => handleChange('search_insurer_name_kana', e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>
        <div>
          <AppAdminFormLabel label="保険種別" />
          <AppSelect
            placeholder="保険種別"
            options={insurerTypeOptions}
            style={{ width: '100%' }}
            value={
              inputFilters.search_insurer_type_id
                ? Number(inputFilters.search_insurer_type_id)
                : undefined
            }
            onChange={val => handleChange('search_insurer_type_id', val ? String(val) : '')}
            allowClear
          />
        </div>
        <div>
          <AppAdminFormLabel label="都道府県" />
          <AppSelect
            placeholder="都道府県"
            options={prefectureOptions}
            style={{ width: '100%' }}
            value={inputFilters.prefecture_id ? Number(inputFilters.prefecture_id) : undefined}
            onChange={val => handleChange('prefecture_id', val ? String(val) : '')}
            allowClear
          />
        </div>
        <div className="flex items-end">
          <AppButton type="primary" icon={<Search />} onClick={handleSearch}>
            検索
          </AppButton>
        </div>
      </div>

      <AppTable
        columns={columns}
        dataSource={data?.data ?? []}
        rowKey="id"
        loading={isLoading}
        pagination={false}
        onRow={r => ({
          onClick: () => onSelect(r as Insurer),
          className: 'cursor-pointer hover:bg-blue-50',
        })}
        locale={{
          emptyText: (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={EMPTY_TEXT_FILTERED} />
          ),
        }}
      />
      <div className="flex justify-end">
        <AppPagination
          current={data?.meta?.current_page ?? 1}
          total={data?.meta?.total ?? 0}
          pageSize={DEFAULT_PER_PAGE}
          onChange={p => setPage(p)}
        />
      </div>
    </div>
  );
};

export default InsuranceCompanySearch;
